(() => {
  if (customElements.get('metrica-notebook-reader')) return;
  class NotebookReader extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.trigger = this.querySelector('.mnr-trigger');
      this.stage = this.querySelector('.mnr-stage');
      this.prev = this.querySelector('.mnr-prev');
      this.next = this.querySelector('.mnr-next');
      this.caption = this.querySelector('.mnr-caption');
      this.unzoom = this.querySelector('.mnr-unzoom');
      this.mq = matchMedia('(max-width:700px)');
      this.trigger.addEventListener('click', () => this.open());
      this.querySelector('.mnr-close').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('close', () => {
        document.body.classList.remove('mnr-open');
        this.trigger.focus({ preventScroll: true });
      });
      this.dialog.addEventListener('click', e => {
        if (e.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) this.dialog.close();
      });
      this.prev.addEventListener('click', () => this.move(-1));
      this.next.addEventListener('click', () => this.move(1));
      this.unzoom.addEventListener('click', () => { this.reading = null; this.render(); });
      this.querySelectorAll('[data-section]').forEach(b => b.addEventListener('click', () => {
        if (!this.data) return;
        const target = { cover: 'cover', archive: 3, intro: 7, ruled: 15, dots: 101 }[b.dataset.section];
        this.index = this.groups.findIndex(g => g.includes(target));
        this.reading = null;
        this.render();
      }));
      this.onResize = () => {
        if (!this.data) return;
        const current = this.reading || this.groups[this.index].at(-1);
        this.setGroups(current);
        this.reading = null;
        this.render();
      };
      this.mq.addEventListener('change', this.onResize);
      this.dialog.addEventListener('keydown', e => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); this.move(e.key === 'ArrowRight' ? 1 : -1); }
      });
    }
    disconnectedCallback() {
      this.mq?.removeEventListener('change', this.onResize);
      if (this.dialog?.open) document.body.classList.remove('mnr-open');
    }
    setGroups(target = 7) {
      this.groups = this.mq.matches ? this.data.groups.flat().map(n => [n]) : this.data.groups;
      this.index = Math.max(0, this.groups.findIndex(g => g.includes(target)));
    }
    async open() {
      if (this.dialog.open) return;
      this.dialog.showModal();
      document.body.classList.add('mnr-open');
      if (this.data) { this.render(); return; }
      await this.load();
    }
    async load() {
      this.stage.replaceChildren();
      this.stage.setAttribute('aria-busy', 'true');
      this.prev.disabled = this.next.disabled = true;
      const status = document.createElement('p');
      status.className = 'mnr-status'; status.setAttribute('role', 'status'); status.textContent = 'Loading the printed pages…';
      this.stage.append(status);
      try {
        const response = await fetch(this.dataset.pagesUrl);
        if (!response.ok) throw new Error('Pages unavailable');
        const data = await response.json();
        if (!data.pages?.length || !data.groups?.length) throw new Error('Empty preview');
        this.data = data;
        this.pageMap = new Map(data.pages.map(p => [p.id, p]));
        this.setGroups(); this.render();
      } catch {
        status.textContent = 'The preview could not load. Please try again.';
        const retry = document.createElement('button'); retry.type = 'button'; retry.className = 'mnr-retry'; retry.textContent = 'Try again'; retry.onclick = () => this.load(); status.append(retry);
      } finally { this.stage.setAttribute('aria-busy', 'false'); }
    }
    move(direction) {
      if (!this.data) return;
      const to = this.index + direction;
      if (to < 0 || to >= this.groups.length) return;
      this.index = to; this.reading = null; this.render();
    }
    render() {
      if (!this.data) return;
      const ids = this.reading ? [this.reading] : this.groups[this.index];
      this.stage.replaceChildren();
      this.stage.classList.toggle('mnr-reading', Boolean(this.reading));
      this.unzoom.hidden = !this.reading;
      ids.forEach(id => {
        const p = this.pageMap.get(id);
        if (this.reading && p.readHtml) {
          const article = document.createElement('article'); article.className = 'mnr-read-text'; article.tabIndex = -1;
          const heading = document.createElement('h3'); heading.textContent = p.title; article.append(heading);
          if (p.subtitle) { const sub = document.createElement('p'); sub.className = 'mnr-read-sub'; sub.textContent = p.subtitle; article.append(sub); }
          const prose = document.createElement('div'); prose.innerHTML = p.readHtml; article.append(prose); this.stage.append(article); return;
        }
        const button = document.createElement('button'); button.type = 'button'; button.className = 'mnr-page';
        button.setAttribute('aria-label', (this.reading ? 'Return from ' : 'Enlarge ') + p.title + (id === 'cover' ? '' : ', page ' + id));
        const img = document.createElement('img'); img.src = p.src; img.alt = p.title; img.width = 1400; img.height = 1986;
        button.append(img);
        button.onclick = () => { this.reading = this.reading ? null : id; this.render(); this.stage.querySelector('article,button')?.focus({ preventScroll: true }); };
        this.stage.append(button);
      });
      this.stage.scrollTop = 0;
      const last = this.pageMap.get(ids.at(-1));
      const title = ids.length === 1 ? last.title : last.section === 'intro' ? 'Inside the introduction' : last.section === 'archive' ? 'From the archive' : last.section === 'ruled' ? 'Pages for your notes' : 'Pages for your sketches';
      const strong = document.createElement('strong'); strong.textContent = title;
      this.caption.replaceChildren(strong, document.createTextNode(ids[0] === 'cover' ? 'Front cover' : 'Interior ' + (ids.length === 1 ? 'page ' + ids[0] : 'pages ' + ids.join('–'))));
      this.prev.disabled = this.index === 0; this.next.disabled = this.index === this.groups.length - 1;
      this.querySelectorAll('[data-section]').forEach(b => b.setAttribute('aria-pressed', b.dataset.section === last.section ? 'true' : 'false'));
    }
  }
  customElements.define('metrica-notebook-reader', NotebookReader);
})();
